"use client";

import React, { useEffect, useState } from "react";
import { Loader, Check, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const animationStyles = `
  @keyframes packet-flow {
    0% { stroke-dashoffset: 1000; }
    100% { stroke-dashoffset: 0; }
  }
  .animate-packet {
    stroke-dasharray: 20 980;
    animation: packet-flow 3s linear infinite;
  }
`;
function JoinNetworkForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="w-full max-w-md mx-auto p-8 rounded-xl border border-primary/20 bg-primary/5 text-center animate-in fade-in zoom-in duration-300">
        <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
          <Check className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Welcome Aboard!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Register Another Person
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel>Organization <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel>Your Role <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none" {...field} value={field.value || ""}>
                    <option value="" disabled>Select your role</option>
                    <option value="Developer">Developer</option>
                    <option value="Engineer">Engineer</option>
                    <option value="Product Manager">Product Manager</option>
                    <option value="Digital Leader">Digital Leader</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="interests" render={() => <FormItem>
            <div className="mb-4">
              <FormLabel>Areas of Interest</FormLabel>
              <FormDescription>Select any topics you’d like to hear about.</FormDescription>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Core Web Vitals", "Page Speed Optimization", "Frontend Best Practices", "Backend Efficiency", "Hosting Strategies", "Tool Comparisons"].map((item, i) => <FormField key={item} control={control} name="interests" render={({
          field
        }) => {
          return <FormItem key={item} className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-input p-3 shadow-xs hover:bg-accent/50 transition-colors" data-index={i}>
                        <FormControl>
                          <input type="checkbox" className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-[var(--color-primary)]" checked={field.value?.includes(item)} onChange={checked => {
                return checked.target.checked ? field.onChange([...(field.value || []), item]) : field.onChange(field.value?.filter((value: string) => value !== item));
              }} />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          {item}
                        </FormLabel>
                      </FormItem>;
        }} />)}
            </div>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive updates."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md bg-muted/30 p-4">
            <FormControl>
              <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-[var(--color-primary)]" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-medium leading-snug">
                I agree to receive updates and newsletters from Web Performance News.
              </FormLabel>
              <FormDescription>
                You can unsubscribe at any time.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-semibold shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : <>
            Subscribe
            <Zap className="ml-2 h-4 w-4 fill-current" />
          </>}
      </Button>
    </Form>;
}
export default function JoinThePerformanceNetwork() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="join-the-performance-network" className="dark bg-background text-foreground relative w-full py-24 overflow-hidden">
      <style>{animationStyles}</style>

      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
        
        {}
        <svg className="absolute top-1/2 left-0 w-full h-[300px] -translate-y-1/2 opacity-40" preserveAspectRatio="none">
          <path d="M0,150 C300,50 900,250 1600,150" fill="none" stroke="var(--color-primary)" strokeWidth="1" className="animate-packet opacity-50" />
          <path d="M0,150 C300,50 900,250 1600,150" fill="none" stroke="var(--color-primary)" strokeWidth="3" className="animate-packet blur-[2px]" />
        </svg>
        
        {}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      </div>

      <div ref={ref} className={`relative container mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
            Ship Faster.
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto">
            Weekly insights on browser APIs, bundling, and latency. Join the network of performance-obsessed developers.
          </p>
        </div>

        <div className="relative z-10 bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6 md:p-8 shadow-2xl">
          <div className="mb-8 text-center md:text-left border-b border-border/50 pb-4">
             <h3 className="text-lg font-semibold text-foreground">Join the Performance Network</h3>
             <p className="text-sm text-muted-foreground">Complete the form below to get started.</p>
          </div>

          <FormProvider formId="join_network_form_home" sectionName="join-network" defaultValues={{
          full_name: "",
          email: "",
          organization: "",
          role: "",
          interests: [],
          consent: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe" successMessage="You've successfully joined the Performance Network! Check your inbox for a confirmation.">
            <JoinNetworkForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}